/* eslint-disable react/prop-types */
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { toast } from 'react-toastify';
import { apipost } from '../../service/api';
import { fmtCurrency } from './statusConfig';

const Row = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value ?? '—'}</Typography>
  </Grid>
);

const ConvertToLoanModal = ({ open, handleClose, lead, onConverted }) => {
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const isPurchase = lead?.loanType === 'Home Purchase';
  const isRefi = lead?.loanType === 'Home Refinance';

  const resetAndClose = () => {
    setConverting(false);
    setResult(null);
    setError(null);
    handleClose();
  };

  const handleConvert = async () => {
    setConverting(true);
    setError(null);
    try {
      const response = await apipost(`bankrate-lead/convert-to-loan/${lead._id}`, {});
      if (response?.status === 200 || response?.status === 201) {
        const body = response.data || {};
        setResult(body);
        toast.success('Loan created in Encompass');
      } else {
        setError(response?.data?.message || 'Conversion failed.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Conversion failed.');
    } finally {
      setConverting(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">Convert to Loan (Encompass)</Typography>
        <ClearIcon onClick={resetAndClose} style={{ cursor: 'pointer' }} />
      </DialogTitle>

      <DialogContent dividers>
        {!result && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              This will create a new loan in <strong>Encompass</strong> using the
              fields below. The Bankrate lead will be marked as{' '}
              <strong>Converted</strong> with a link to the Encompass loan id.
              No local loan record is created.
            </Alert>

            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Encompass Payload Preview
            </Typography>
            <Grid container spacing={2}>
              <Row label="Borrower" value={[lead.firstName, lead.lastName].filter(Boolean).join(' ')} />
              <Row label="Email" value={lead.email} />
              <Row label="Phone" value={lead.phone} />
              <Row label="Loan Type" value={lead.loanType} />
              <Row label="Loan Amount" value={fmtCurrency(lead.loanAmount)} />
              <Row label="Credit Score" value={lead.creditProfileAmount} />
              <Row label="Annual Income" value={fmtCurrency(lead.grossAnnualIncomeAmount)} />
              <Row label="Property City" value={lead.propertyCity} />
              <Row label="Property State" value={lead.propertyState} />
              <Row label="Property ZIP" value={lead.zipCode} />
              <Row label="Property Type" value={lead.propertyType} />
              <Row label="Property Use" value={lead.propertyUse} />
              {isPurchase && (
                <>
                  <Row label="Sales Price" value={fmtCurrency(lead.purchasePriceAmount)} />
                  <Row label="Down Payment" value={fmtCurrency(lead.downPaymentAmount)} />
                </>
              )}
              {isRefi && (
                <>
                  <Row label="Estimated Value" value={fmtCurrency(lead.propertyEstimatedValueAmount)} />
                  <Row label="1st Mortgage Balance" value={fmtCurrency(lead.firstMortgageBalanceAmount)} />
                  <Row label="Cash Out" value={fmtCurrency(lead.additionalCashAmount)} />
                </>
              )}
              <Row label="Bankrate Lead ID" value={lead.leadId} />
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}

        {result && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Loan successfully created in Encompass.
            </Alert>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Encompass Response
            </Typography>
            <Grid container spacing={2}>
              <Row
                label="Encompass Loan ID"
                value={
                  result.convertedLoanId ||
                  result.encompass?.loanGuid ||
                  result.encompass?.loanId ||
                  result.encompass?.guid ||
                  result.encompass?.id
                }
              />
              {result.encompass?.loanNumber && (
                <Row label="Loan Number" value={result.encompass.loanNumber} />
              )}
              {result.encompass?.loanFolder && (
                <Row label="Loan Folder" value={result.encompass.loanFolder} />
              )}
            </Grid>
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Full Response
              </Typography>
              <Box
                component="pre"
                sx={{
                  fontSize: 12,
                  bgcolor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 240,
                }}
              >
                {JSON.stringify(result, null, 2)}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!result ? (
          <Stack direction="row" spacing={1}>
            <Button onClick={resetAndClose} disabled={converting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConvert}
              disabled={converting}
              startIcon={converting ? <CircularProgress size={16} /> : <SwapHorizIcon />}
            >
              {converting ? 'Creating in Encompass…' : 'Create Loan in Encompass'}
            </Button>
          </Stack>
        ) : (
          <Button
            variant="contained"
            onClick={() => {
              onConverted?.();
              resetAndClose();
            }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConvertToLoanModal;
